// client/src/components/license/LicenseTypeSelector.js
import React from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Chip,
  Typography,
} from "@mui/material";
import { LicenseType, LicenseTypeLabels } from "../../constants/licenseTypes";

const LicenseTypeSelector = ({
  value,
  onChange,
  disabled = false,
  required = false,
  error = false,
  helperText = "",
  size = "medium",
  fullWidth = true,
  label = "License Type",
}) => {
  const getLicenseTypeDescription = (type) => {
    const descriptions = {
      [LicenseType.UNKNOWN]: "Unspecified license type",
      [LicenseType.TRIAL]: "Temporary evaluation license",
      [LicenseType.NFR]: "Not for resale license",
      [LicenseType.EDUCATIONAL]: "Educational institution license",
      [LicenseType.SUBSCRIPTION]: "Commercial subscription license",
    };
    return descriptions[type] || "No description available";
  };

  const getLicenseTypeColor = (type) => {
    const colors = {
      [LicenseType.UNKNOWN]: "default",
      [LicenseType.TRIAL]: "warning",
      [LicenseType.NFR]: "info",
      [LicenseType.EDUCATIONAL]: "secondary",
      [LicenseType.SUBSCRIPTION]: "success",
    };
    return colors[type] || "default";
  };

  return (
    <Box>
      <FormControl
        fullWidth={fullWidth}
        error={error}
        size={size}
        disabled={disabled}
      >
        <InputLabel required={required}>{label}</InputLabel>
        <Select
          value={value || ""}
          onChange={(e) => onChange(parseInt(e.target.value))}
          label={label}
          required={required}
          renderValue={(selected) => (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                label={LicenseTypeLabels[selected]}
                color={getLicenseTypeColor(selected)}
                size="small"
                sx={{ height: 24 }}
              />
              <Typography variant="body2" color="text.secondary">
                {getLicenseTypeDescription(selected)}
              </Typography>
            </Box>
          )}
        >
          <MenuItem value="" disabled>
            <em>Select License Type</em>
          </MenuItem>
          {Object.entries(LicenseType).map(([key, value]) => (
            <MenuItem key={key} value={value}>
              <Box sx={{ py: 1, width: "100%" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Chip
                    label={LicenseTypeLabels[value]}
                    color={getLicenseTypeColor(value)}
                    size="small"
                    sx={{ height: 20 }}
                  />
                  <Typography variant="body2" fontWeight="medium">
                    {LicenseTypeLabels[value]}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", ml: 3 }}
                >
                  {getLicenseTypeDescription(value)}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>

      {/* Current selection info */}
      {value !== undefined && value !== "" && (
        <Box sx={{ mt: 1, p: 1, bgcolor: "action.hover", borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Selected: <strong>{LicenseTypeLabels[value]}</strong> -{" "}
            {getLicenseTypeDescription(value)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LicenseTypeSelector;
